import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Eye, Send, Loader2, Bug, Lightbulb, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (s) => {
  if (s == null) return "bg-stone-100 text-stone-500";
  if (s >= 8) return "bg-emerald-100 text-emerald-700";
  if (s >= 6) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

const sevBadge = { alta: "bg-red-100 text-red-700", media: "bg-amber-100 text-amber-700", baixa: "bg-stone-100 text-stone-600" };

function Evidence({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {items.map((e, i) => (
        <blockquote key={i} className="text-xs text-stone-600 italic border-l-2 border-[#D4651A] bg-orange-50/60 pl-3 pr-2 py-1.5 rounded-r">
          "{e}"
        </blockquote>
      ))}
    </div>
  );
}

const fmtDt = (iso) => {
  if (!iso) return "?";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const fmtWindow = (r) =>
  r?.window_start ? `Período: ${fmtDt(r.window_start)} até ${fmtDt(r.window_end)}` : "Histórico completo";

function ReportDetail({ report, onClose }) {
  const r = report?.result || {};
  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-report-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1B3A2F]">
            Auditoria · {report?.user_name} · {report?.audit_date}
          </DialogTitle>
          <DialogDescription>
            {fmtWindow(report)} · {report?.stats?.total_messages || 0} mensagens
            ({report?.stats?.audio_messages || 0} de áudio) · {report?.stats?.crm_events || 0} eventos CRM
          </DialogDescription>
        </DialogHeader>

        {report?.status === "failed" ? (
          <div className="text-sm text-red-600">A auditoria falhou: {report?.error}</div>
        ) : report?.status === "empty" ? (
          <div className="text-sm text-stone-500">Sem conversas no período analisado.</div>
        ) : (
          <div className="space-y-5 text-sm">
            <div className="flex items-center gap-3">
              <span className={`text-2xl font-bold px-4 py-2 rounded-xl ${scoreColor(r.score)}`} data-testid="audit-detail-score">
                {r.score != null ? `${r.score}/10` : "n/a"}
              </span>
              <p className="text-stone-500 text-xs flex-1">{r.score_rationale}</p>
            </div>

            <section>
              <h3 className="font-semibold text-[#1B3A2F] mb-1">Resumo executivo</h3>
              <p className="text-stone-700 whitespace-pre-wrap">{r.executive_summary}</p>
            </section>

            <section>
              <h3 className="font-semibold text-[#1B3A2F] mb-2 flex items-center gap-2">
                <Bug className="w-4 h-4 text-red-500" /> Bugs encontrados ({(r.bugs || []).length})
              </h3>
              {(r.bugs || []).length === 0 && <p className="text-stone-400 text-xs">Nenhum bug encontrado.</p>}
              <div className="space-y-3">
                {(r.bugs || []).map((b, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#1B3A2F]">{b.numero}. {b.titulo}</span>
                      <Badge className={sevBadge[b.severidade] || sevBadge.media}>{b.severidade}</Badge>
                    </div>
                    <p className="text-stone-600 mt-1">{b.descricao}</p>
                    <Evidence items={b.evidencia} />
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-[#1B3A2F] mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" /> Melhorias priorizadas ({(r.melhorias || []).length})
              </h3>
              <div className="space-y-3">
                {(r.melhorias || []).map((m, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#1B3A2F]">{m.numero}. {m.titulo}</span>
                      <Badge variant="outline">{m.categoria}</Badge>
                      <Badge className={m.impacto === "alto" ? "bg-red-100 text-red-700" : m.impacto === "medio" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}>
                        impacto {m.impacto}
                      </Badge>
                    </div>
                    <p className="text-stone-600 mt-1">{m.descricao}</p>
                    <Evidence items={m.evidencia} />
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-[#1B3A2F] mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-500" /> Novas funcionalidades sugeridas ({(r.novas_funcionalidades || []).length})
              </h3>
              <div className="space-y-3">
                {(r.novas_funcionalidades || []).map((f, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <span className="font-medium text-[#1B3A2F]">{f.numero}. {f.titulo}</span>
                    <p className="text-stone-600 mt-1">{f.descricao}</p>
                    {f.justificativa && <p className="text-stone-500 text-xs mt-1">Justificativa: {f.justificativa}</p>}
                    <Evidence items={f.evidencia} />
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-[#1B3A2F] mb-1 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" /> Comparação com auditorias anteriores
              </h3>
              <p className="text-stone-600">{r.comparacao_anterior}</p>
            </section>

            <section>
              <h3 className="font-semibold text-[#1B3A2F] mb-1">Plano de ação recomendado</h3>
              <ol className="space-y-1">
                {(r.plano_de_acao || []).map((s, i) => (
                  <li key={i} className="text-stone-700">{s}</li>
                ))}
              </ol>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminAuditReports({ refreshKey }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/audit/reports");
      setReports(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const openDetail = async (r) => {
    const res = await api.get(`/admin/audit/reports/${r.id}`);
    setDetail(res.data);
  };

  const downloadPdf = async (r) => {
    setBusyId(r.id + "-pdf");
    try {
      const res = await api.get(`/admin/audit/reports/${r.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `auditoria-${r.audit_date}-${(r.user_name || "corretor").split(" ")[0].toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao gerar PDF"); }
    finally { setBusyId(null); }
  };

  const sendWa = async (r) => {
    setBusyId(r.id + "-wa");
    try {
      const res = await api.post(`/admin/audit/reports/${r.id}/send-whatsapp`);
      res.data.sent ? toast.success(`Relatório enviado pro WhatsApp ${res.data.phone}`) : toast.warning("Envio não confirmado (verifica o ChatPro)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no envio"); }
    finally { setBusyId(null); }
  };

  if (loading) return <div className="py-12 text-center text-stone-400">Carregando relatórios...</div>;

  return (
    <div className="space-y-3" data-testid="audit-reports-list">
      {reports.length === 0 && (
        <Card className="p-8 text-center text-stone-400" data-testid="audit-reports-empty">
          Nenhuma auditoria rodou ainda. Clica em "Rodar auditoria agora" ou aguarda a rotina diária das 20h.
        </Card>
      )}
      {reports.map((r) => {
        const score = r.result?.score;
        const bugs = (r.result?.bugs || []).length;
        const melhorias = (r.result?.melhorias || []).length + (r.result?.novas_funcionalidades || []).length;
        return (
          <Card key={r.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`audit-report-card-${r.id}`}>
            <span className={`text-xl font-bold px-3 py-2 rounded-xl min-w-[76px] text-center ${scoreColor(score)}`}>
              {r.status === "empty" ? "vazio" : r.status === "failed" ? "erro" : score != null ? `${score}` : "?"}
            </span>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-[#1B3A2F]">{r.user_name} · {r.audit_date}</div>
              <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                <Badge variant="outline">{r.trigger === "scheduled" ? "automática (legado)" : "manual"}</Badge>
                {r.first_audit && <Badge variant="outline">1ª auditoria (24h)</Badge>}
                <span data-testid={`audit-report-window-${r.id}`}>{fmtWindow(r)}</span>
                <span>{r.stats?.total_messages || 0} msgs · {r.stats?.audio_messages || 0} áudios · {r.stats?.crm_events || 0} eventos CRM</span>
                {r.status === "completed" && <span>· {bugs} bugs · {melhorias} melhorias</span>}
                {r.wa_sent === true && <span className="text-emerald-600">· enviado no WhatsApp</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`audit-report-view-${r.id}`} className="gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Ver
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadPdf(r)} disabled={busyId === r.id + "-pdf"}
                data-testid={`audit-report-pdf-${r.id}`} className="gap-1.5">
                {busyId === r.id + "-pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => sendWa(r)} disabled={busyId === r.id + "-wa"}
                data-testid={`audit-report-send-${r.id}`} className="gap-1.5">
                {busyId === r.id + "-wa" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} WhatsApp
              </Button>
            </div>
          </Card>
        );
      })}
      <ReportDetail report={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
